// ═══════════════════════════════════════════════════
//  Tags Map Tab — Product Tags Distribution
// ═══════════════════════════════════════════════════
import React from "react";
import LocationMap from "../LocationMap";
import { Package, MapPin } from "lucide-react";

import { ScanData } from "./types";
import { getTaggedLocations } from "./helpers";

interface TagsMapTabProps {
  scanData: ScanData;
  mapLocations: Array<{ latitude: number; longitude: number; city?: string; tagId: string }>;
  loadingMap: boolean;
}

const TagsMapTab: React.FC<TagsMapTabProps> = ({ scanData, mapLocations, loadingMap }) => {
  const productId = scanData?.product?.productId || scanData?.tagId;
  const taggedLocations = getTaggedLocations(scanData);

  // Merge geoStats + API locations, dedupe by tagId
  const allLocations = [
    ...taggedLocations,
    ...mapLocations.map((loc) => ({
      latitude: loc.latitude,
      longitude: loc.longitude,
      label: `${loc.city || "Unknown"} - ${loc.tagId}`,
      tagId: loc.tagId,
    })),
  ];
  const uniqueLocations = Array.from(
    new Map(allLocations.map((item) => [item.tagId, item])).values()
  );
  const uniqueCities = uniqueLocations.length > 0
    ? new Set(uniqueLocations.map((l) => l.label?.split(" - ")[0]).filter(Boolean)).size
    : 0;

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <Package className="w-6 h-6 text-purple-600" />
        <h3 className="text-xl font-bold text-gray-900">Product Tags Distribution</h3>
        {productId && (
          <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs font-medium">
            Product ID: {productId}
          </span>
        )}
        {loadingMap && <span className="text-sm text-gray-500">Loading map data...</span>}
      </div>

      {uniqueLocations.length > 0 ? (
        <div className="space-y-4">
          <LocationMap points={uniqueLocations} height="350px" zoom={4} />

          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-semibold text-purple-900 mb-3">Distribution Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-sm">
              <div>
                <div className="text-2xl font-bold text-purple-600">{uniqueLocations.length}</div>
                <div className="text-purple-700">Total Tags</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">{scanData?.stats?.totalScans || 0}</div>
                <div className="text-purple-700">Total Scans</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">{uniqueCities}</div>
                <div className="text-purple-700">Cities</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">{scanData?.stats?.geoStats?.countries?.length || 0}</div>
                <div className="text-purple-700">Countries</div>
              </div>
            </div>

            {uniqueLocations.length > 0 && (
              <div className="mt-4 space-y-2">
                <h5 className="font-medium text-purple-900">Tagged Locations:</h5>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {uniqueLocations.map((location, index) => (
                    <div key={index} className="flex justify-between items-center py-1 px-2 bg-white rounded text-xs">
                      <span className="text-purple-800">{location.label}</span>
                      <span className="text-purple-600 font-mono">{location.tagId}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <h4 className="text-lg font-semibold text-gray-600 mb-2">
            {loadingMap ? "Loading locations..." : "No Tagged Locations Available"}
          </h4>
          <p className="text-sm">Tagged locations for this product will appear here when available</p>
        </div>
      )}
    </div>
  );
};

export default TagsMapTab;
